// Tab-completion logic for the Terminal. Pure — no DOM, no fs, no imports — so
// the fiddly parts (which token is being completed, how far a common prefix
// gets you) are unit-testable. Sourcing the candidates is the caller's job.
// KEEP IN SYNC with ./shell-complete.d.mts.

/** Longest common prefix of a list of strings ("" when there isn't one). */
export function commonPrefix(items) {
  if (!Array.isArray(items) || items.length === 0) return "";
  let out = String(items[0] ?? "");
  for (let i = 1; i < items.length; i++) {
    const s = String(items[i] ?? "");
    let n = 0;
    while (n < out.length && n < s.length && out[n] === s[n]) n++;
    out = out.slice(0, n);
    if (!out) break;
  }
  return out;
}

/**
 * Which token Tab should act on.
 *
 * A trailing space means the user has finished a word and is starting a new
 * (empty) one — `cd ` completes everything in the directory, not the word "cd".
 * `index` is where the token starts, so the caller can splice a replacement in
 * without re-parsing.
 */
export function completionTarget(line) {
  const text = typeof line === "string" ? line : "";
  const index = text.lastIndexOf(" ") + 1; // 0 when there is no space at all
  const token = text.slice(index);
  // First word only if nothing precedes it — `ls` is a command, `cd ls` is not.
  const isFirstWord = text.slice(0, index).trim() === "";
  return { isFirstWord, token, index };
}

/**
 * Apply `candidates` to `token`.
 *
 * Shell convention: one match completes; several complete as far as the shared
 * prefix goes and then list. `suggestions` is non-empty only when the user has
 * to choose — so the caller shows them only when completion stalled.
 */
export function completeToken(token, candidates) {
  const t = typeof token === "string" ? token : "";
  const all = Array.isArray(candidates) ? candidates.map(String) : [];
  const matches = all.filter((c) => c.startsWith(t));
  if (matches.length === 0) return { value: t, suggestions: [] };
  if (matches.length === 1) return { value: matches[0], suggestions: [] };
  const prefix = commonPrefix(matches);
  // Only list when completing didn't get us anywhere new, matching bash: the
  // first Tab extends, a second Tab (no progress) shows the options.
  return {
    value: prefix.length > t.length ? prefix : t,
    suggestions: prefix.length > t.length ? [] : matches,
  };
}

/**
 * Split a path token into the directory part to list and the basename to match.
 * `"sub/fi"` → `{ dir: "sub/", base: "fi" }`; `"fi"` → `{ dir: "", base: "fi" }`.
 * `dir` keeps its trailing slash so the caller can re-join by concatenation.
 */
export function splitPathToken(token) {
  const t = typeof token === "string" ? token : "";
  const cut = t.lastIndexOf("/");
  if (cut < 0) return { dir: "", base: t };
  return { dir: t.slice(0, cut + 1), base: t.slice(cut + 1) };
}
